


import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  computed,
  reactive,
  ref,
} from '@vue/composition-api';

import type {
  HokkaidoAirportReport,
  HokkaidoAirportState,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type { HokkaidoAirportCar } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { UserActionTypes } from '@/store/modules/user';
import useGeolocation from '@/composables/externalReports/useGeolocation';
import hokkaidoAirportCarApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_car';
import hokkaidoAirportMtxApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_mtx';
import { enableNoSleep, disableNoSleep } from '@/lib/noSleepUtil';
import {
  getHokkaidoAirportScreenName,
  initCommonState,
  notifyWorkElapsedTimeSpeech,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/utils';
import type { HokkaidoAirportScreenName } from '@/components/Sp/ExternalReports/HokkaidoAirport/types';
import { useLastStandbyType } from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useLastStandbyType';
import {
  useLastVehicleSelection,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useLastVehicleSelection';
import {
  useHokkaidoAirportReportStorage,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useHokkaidoAirportReportStorage';
import {
  useHokkaidoAirportReportCreation,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useHokkaidoAirportReportCreation';
import {
  roundToIntervalMinutes,
  timeDifferenceInSeconds,
  secondsToTimeInteger,
  timeInteger,
} from '@/lib/dateTimeHelper';
import useSpeech from '@/composables/useSpeech';
import type {
  HokkaidoAirportMtxRequest,
  HokkaidoAirportMtxsCreateParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportMtxRequest';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import DepartureScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/DepartureScreen/index.vue';
import StandbyScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/StandbyScreen/index.vue';
import WorkingScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/WorkingScreen/index.vue';
import PostWorkInspectionScreen
  from '@/components/Sp/ExternalReports/HokkaidoAirport/PostWorkInspectionScreen/index.vue';
import PostWorkScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/PostWorkScreen/index.vue';
import SaveWorkSituationScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/SaveWorkSituation/index.vue';
import PhotoMemoScreen from '@/components/Sp/ExternalReports/HokkaidoAirport/PhotoMemoScreen/index.vue';
import { dtFormat } from '@/lib/dateHelper';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import { EXTERNAL_REPORT_ABILITY_DRIVER } from '@/consts/externalReports/external_reports';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import {
  isHokkaidoAirportExternalReportSettings,
} from '@/lib/externalReports/externalReportHelper';
import { EXTERNAL_REPORT_TYPE, REPORT_TS_ROUND_MINUTES } from '@/consts/externalReports/hokkaidoAirport';

const CAR_UPDATE_TIMER_SEC = 10;
const WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC = 1;
const MTX_STORE_TIMER_SEC = 5;
const MTX_CREATE_TIMER_SEC = 60;

export default defineComponent({
  components: {
    DepartureScreen,
    StandbyScreen,
    WorkingScreen,
    PostWorkInspectionScreen,
    PostWorkScreen,
    SaveWorkSituationScreen,
    PhotoMemoScreen,
  },
  setup() {
    const state = reactive<HokkaidoAirportState>(initCommonState());
    const store = useStore();
    const { router } = useRoute();
    const userState = store.state.user;
    const { doSpeech } = useSpeech();
    const { lastStandbyType, saveLastStandbyType } = useLastStandbyType();
    const { lastVehicleSelection, saveLastVehicleSelection } = useLastVehicleSelection();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      showErrorMsg(errorMessage);
    });

    const toDateOrNull = (value: Date | string | null): Date | null => {
      if (!value) return null;
      if (value instanceof Date) return value;

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const workElapsedTimeInt = computed<number>(() => {
      const workStartTime = toDateOrNull(state.workStartTime);
      const workEndTime = toDateOrNull(state.workEndTime ?? state.currentTime);
      if (!workStartTime || !workEndTime) { return 0; }

      const workElapsedTimeSec = timeDifferenceInSeconds(workStartTime, workEndTime);
      return secondsToTimeInteger(workElapsedTimeSec);
    });
    const hokkaidoAirportUserSettings = ref<HokkaidoAirportSettings | null>(null);
    const externalReportAbility = computed<ExternalReportAbility | null>(() => {
      return userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
    });
    const statusWorking = computed<string>(() => {
      return hokkaidoAirportUserSettings.value?.choices.status.working.key ?? 'working';
    });
    const statusRunning = computed<string>(() => {
      return hokkaidoAirportUserSettings.value?.choices.status.running.key ?? 'moving';
    });
    const statusStopped = computed<string>(() => {
      return hokkaidoAirportUserSettings.value?.choices.status.stopped.key ?? 'stopped';
    });
    const currentComponent = computed<string>(() => {
      if (state.currentScreenOverride) {
        return state.currentScreenOverride;
      }

      return reportScreenName.value;
    });
    const isImagesEditing = computed<boolean>(() => {
      return state.currentScreenOverride === 'PhotoMemoScreen';
    });

    const stopAll = async() => {
      clearTimers();
      await doRequest(async() => {
        await updateCarStatus(statusStopped.value);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const geolocationState = useGeolocation(stopAll);
    const currentLocation = computed(() => geolocationState.currentLocation);

    const {
      saveLocalStorage,
      clearLocalStorage,
      restoreLocalStorage,
      stopLocalStorageSaving,
    } = useHokkaidoAirportReportStorage(state);
    const reportScreenName = computed<HokkaidoAirportScreenName>(() => {
      return getHokkaidoAirportScreenName(state.hokkaidoAirportCar.report);
    });
    const getScreenStatus = (screenName: HokkaidoAirportScreenName): string => {
      if (screenName === 'DepartureScreen') return statusStopped.value;
      if (screenName === 'WorkingScreen') return statusWorking.value;

      return statusRunning.value;
    };
    const updateCommonState = (status: string | null, report: HokkaidoAirportReport): void => {
      state.status = status ?? state.status;
      state.hokkaidoAirportCar.report = report;
      state.currentScreenOverride = null;
      saveLocalStorage();
      saveLastStandbyType(report.standbyType);
      saveLastVehicleSelection({ airportName: report.airportName, carType: report.carType });
    };
    const updateCommonStateAndCarStatus = async(status: string, report: HokkaidoAirportReport): Promise<void> => {
      updateCommonState(status, report);
      await doRequest(async() => {
        await updateCarStatus(status);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const updateCarStatus = async(status: string): Promise<HokkaidoAirportCar | null> => {
      if (!geolocationState.currentLocation) { return null; }

      const { lat, lon } = geolocationState.currentLocation;
      state.status = status ?? state.status;
      const reqObj = {
        lat: lat,
        lon: lon,
        status: state.status,
      };
      const hokkaidoAirportCar = await hokkaidoAirportCarApi.createOrUpdate(reqObj);
      state.hokkaidoAirportCar.id = hokkaidoAirportCar.id;
      state.hokkaidoAirportCar.currentReportId = hokkaidoAirportCar.currentReportId;
      saveLocalStorage();
      return hokkaidoAirportCar;
    };
    const clearTimers = () => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.carUpdateTimer = null;
      state.workElapsedTimeNotifyTimer = null;
      state.mtxStoreTimer = null;
      state.mtxCreateTimer = null;
    };
    const storeHokkaidoAirportMtx = () => {
      if (!geolocationState.currentLocation) { return; }
      if (state.hokkaidoAirportCar.id === 0 || state.hokkaidoAirportCar.report.id === 0) { return; }

      const now = new Date();
      if (state.lastHokkaidoAirportMtxTs) {
        const thres = 1000;
        const diffMSec = now.getTime() - state.lastHokkaidoAirportMtxTs.getTime();
        if (diffMSec < thres) { return; }
      }

      const { lat, lon } = geolocationState.currentLocation;
      const obj: HokkaidoAirportMtxRequest = {
        ts: now,
        lat: lat,
        lon: lon,
        status: state.status,
      };
      state.hokkaidoAirportMtxs.push(obj);
      state.lastHokkaidoAirportMtxTs = now;
    };

    const { ensureReport } = useHokkaidoAirportReportCreation(state, {
      getHanName: () => externalReportAbility.value?.han_name,
      updateCarStatus,
      saveLocalStorage,
    });
    const startStandby = async(status: string, report: HokkaidoAirportReport): Promise<void> => {
      // 車両の状態更新が失敗しても画面は待機中に切り替わるため、位置送信は必ず再開する
      try {
        await updateCommonStateAndCarStatus(status, report);
      } finally {
        clearTimers();
        await restartPositionIntervals();
      }
    };
    const startWorking = async(status: string, report: HokkaidoAirportReport): Promise<void> => {
      // 車両の状態更新が失敗しても画面は作業中に切り替わるため、位置送信は必ず再開する
      try {
        await updateCommonStateAndCarStatus(status, report);
      } finally {
        state.workStartTime = new Date();
        state.workEndTime = null;
        // 待機中に蓄積したMTX（最大約12点）を先に送る。送信に失敗しても作業開始は止めない（作業開始はサーバーに保存済み）
        try {
          await saveHokkaidoAirportMtxs();
        } catch (error) {
          // 失敗したMTXは再送しない
        }
        await restartAllIntervals();
      }
    };
    const finishPostWorkInspection = (report: HokkaidoAirportReport): void => {
      updateCommonState(null, report);
    };
    const saveHokkaidoAirportMtxs = async() => {
      if (state.hokkaidoAirportMtxs.length < 2) { return; }

      const hokkaidoAirportMtxs = state.hokkaidoAirportMtxs.splice(0);
      const reqObj: HokkaidoAirportMtxsCreateParams = {
        car_id: state.hokkaidoAirportCar.id,
        report_id: state.hokkaidoAirportCar.report.id,
        mtxs: hokkaidoAirportMtxs,
      };
      await hokkaidoAirportMtxApi.createHokkaidoAirportMTXs(reqObj);
    };
    const editContentAndRoad = () => {
      state.currentScreenOverride = 'SaveWorkSituationScreen';
    };
    const addPhotoMemo = () => {
      state.currentScreenOverride = 'PhotoMemoScreen';
    };
    const notifyWorkElapsedTime = () => {
      state.currentTime = new Date();
      notifyWorkElapsedTimeSpeech(workElapsedTimeInt.value, doSpeech);
    };
    const doStopWorking = async() => {
      await doRequest(async() => {
        await Promise.all([
          saveHokkaidoAirportMtxs(),
          updateCarStatus(statusRunning.value),
        ]);
      }, '作業終了に失敗しました。再度操作を行ってください。');
    };
    const suspendWork = async(status: string, report: HokkaidoAirportReport) => {
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      state.workElapsedTimeNotifyTimer = null;
      await doStopWorking();
      updateCommonState(status, report);
      state.workEndTime = new Date();
    };
    const startCarStatusLoop = () => {
      const loop = async() => {
        try {
          await updateCarStatus(state.status);
        } catch {
          // 車両の状態更新に失敗しても次のループで再試行する
        }

        state.carUpdateTimer = window.setTimeout(loop, CAR_UPDATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartHokkaidoAirportCarUpdateInterval = async() => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      startCarStatusLoop();
    };
    const restartWorkElapsedTimeNotifyInterval = () => {
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      state.workElapsedTimeNotifyTimer = window.setInterval(notifyWorkElapsedTime, WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC * 1000);
    };
    const startMtxCreateLoop = () => {
      const loop = async() => {
        try {
          await saveHokkaidoAirportMtxs();
        } catch {
          // 失敗しても次のループで送信を続ける
        }

        state.mtxCreateTimer = window.setTimeout(loop, MTX_CREATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartHokkaidoAirportMtxUpdateInterval = () => {
      state.hokkaidoAirportMtxs = [];
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.mtxStoreTimer = window.setInterval(storeHokkaidoAirportMtx, MTX_STORE_TIMER_SEC * 1000);
      startMtxCreateLoop();
    };
    // 位置送信とMTX蓄積。待機中・作業中・終了時点検・作業完了で動かす
    const restartPositionIntervals = async() => {
      await restartHokkaidoAirportCarUpdateInterval();
      restartHokkaidoAirportMtxUpdateInterval();
    };
    // 作業中だけ、位置送信とMTX蓄積に加えて経過時間の読み上げも動かす
    const restartAllIntervals = async() => {
      clearTimers();
      await restartPositionIntervals();
      restartWorkElapsedTimeNotifyInterval();
    };
    const showErrorMsg = (errMsg: string) => {
      state.errorModalMsg = errMsg;
      state.showErrorModal = true;
    };
    const logout = async() => {
      try {
        await stopAll();
      } catch {
        // 車両の状態更新に失敗してもログアウトは続ける
      }
      await doRequest(async() => {
        await store.dispatch(UserActionTypes.LOGOUT);
      }, 'ログアウトに失敗しました。再度操作を行ってください。');
      router.replace({ name: 'Login' });
    };
    // 業務終了後のログアウトでは、次のログインで新しい報告書を作るよう localStorage を消す
    const logoutAfterBaseArrival = async() => {
      try {
        await logout();
      } finally {
        clearLocalStorage();
      }
    };

    onMounted(async() => {
      enableNoSleep();
      await doRequest(async() => {
        await waitForUserAndMasters();
        if (restoreLocalStorage()) {
          state.status = getScreenStatus(reportScreenName.value);
        }
        const externalReportSetting = window.master.externalReportSettings?.find(
          (setting) => setting.external_report_type === EXTERNAL_REPORT_TYPE,
        );
        if (!externalReportSetting) return;

        hokkaidoAirportUserSettings.value = isHokkaidoAirportExternalReportSettings(externalReportSetting)
          ? externalReportSetting
          : null;
        if (!externalReportAbility.value || !hokkaidoAirportUserSettings.value) {
          router.replace({ name: 'Login' });
          return;
        }
        const abilities = hokkaidoAirportUserSettings.value?.choices.abilities;
        const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
        if (!ability) {
          router.replace({ name: 'Login' });
          return;
        }
        if (!ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
          router.replace({ name: 'Login' });
          return;
        }
        if (!state.baseDepartureTime) {
          state.baseDepartureTime = roundToIntervalMinutes(new Date(), REPORT_TS_ROUND_MINUTES);
          saveLocalStorage();
        }
        if (state.hokkaidoAirportCar.report.id !== 0 && state.status !== statusStopped.value) {
          if (reportScreenName.value === 'WorkingScreen') {
            await restartAllIntervals();
          } else {
            clearTimers();
            await restartPositionIntervals();
          }
        }
        if (!geolocationState.currentLocation) {
          let count = 100;
          while (count-- > 0 && !geolocationState.currentLocation) {
            await new Promise(resolve => window.setTimeout(resolve, 100));
          }
        }
        await updateCarStatus(state.status);
      }, 'ユーザー情報の読み込みに失敗しました。再度操作を行ってください。');
      if (!hokkaidoAirportUserSettings.value) {
        router.replace({ name: 'Login' });
        return;
      }

      state.externalReportSpLabels = hokkaidoAirportUserSettings.value.sp;
      try {
        await doRequest(async() => {
          await ensureReport();
        }, '報告書の作成に失敗しました。');
      } catch {
        // 再操作は完了・スキップの押下時に ensureReport が行う
      }
    });

    onBeforeUnmount(() => {
      stopLocalStorageSaving();
      clearTimers();
      disableNoSleep();
    });

    return {
      state,
      geolocationState,
      currentComponent,
      currentLocation,
      isRequesting,
      isImagesEditing,
      userState,
      workElapsedTimeInt,
      statusStopped,
      externalReportAbility,
      hokkaidoAirportUserSettings,
      addPhotoMemo,
      doStopWorking,
      dtFormat,
      editContentAndRoad,
      ensureReport,
      finishPostWorkInspection,
      lastStandbyType,
      lastVehicleSelection,
      logout,
      logoutAfterBaseArrival,
      showErrorMsg,
      startStandby,
      suspendWork,
      startWorking,
      timeInteger,
      updateCommonState,
      updateCommonStateAndCarStatus,
    };
  },
});
